import React, { useState } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from "recharts";
import "./MonitoringStats.scss";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import useMediaQuery from "@mui/material/useMediaQuery";
import Loading from "../common/loading";
import NoData from "../common/NoData";
import ChartError from "../common/ChartError";
import { useChartTooltip, ChartHoverTooltip } from "./ChartHoverTooltip";

// Same speech-bubble shape (rounded box + down-pointing arrow) used by both
// the always-on "Total Monitoring Hours" label and the hover-only tooltip
// for the other two series — just re-anchored and recolored per use.
const speechBubblePath = (cx, apexY, scale = 1) => `
  M ${cx - 43.5 * scale} ${apexY - 30 * scale}
  h ${87 * scale}
  a ${4 * scale} ${4 * scale} 0 0 1 ${4 * scale} ${4 * scale}
  v ${18 * scale}
  a ${4 * scale} ${4 * scale} 0 0 1 ${-4 * scale} ${4 * scale}
  h ${-39.5 * scale}
  l ${-4 * scale} ${6 * scale}
  l ${-4 * scale} ${-6 * scale}
  h ${-39.5 * scale}
  a ${4 * scale} ${4 * scale} 0 0 1 ${-4 * scale} ${-4 * scale}
  v ${-18 * scale}
  a ${4 * scale} ${4 * scale} 0 0 1 ${4 * scale} ${-4 * scale}
  Z
`;

// Shared bubble rendering — width, font size, font color, truncation, all of
// it — so every series' bubble is pixel-identical except for its fill color.
const renderSpeechBubble = ({
  cx,
  adjustedY,
  value,
  fill,
  isCompact,
  showTooltip,
  hideTooltip,
}) => {
  // Format and truncate value - FIX: Check original value length first
  const originalValueStr = value.toString();
  const maxDigits = 8;
  const isTruncated = originalValueStr.length > maxDigits;

  let displayValue;
  if (isTruncated) {
    displayValue = `${originalValueStr.slice(0, maxDigits)}... hrs`;
  } else {
    displayValue = `${Number(value).toFixed(2)} hrs`;
  }

  const fullValue = `${Number(value).toFixed(2)} hrs`;
  const scale = isCompact ? 0.75 : 1;
  // Tinted shadow matching the bubble's own color (Figma: drop shadow
  // 0/8/16/-2 colored to match the tooltip's fill, not a plain black shadow).
  const shadowColor = fill.replace(", 1)", ", 0.5)");

  return (
    <g style={{ filter: `drop-shadow(0px 8px 14px ${shadowColor})` }}>
      <path d={speechBubblePath(cx, adjustedY, scale)} fill={fill} />
      <text
        x={cx}
        y={adjustedY - 14 * scale}
        fill="rgba(25, 25, 25, 1)"
        fontSize={isCompact ? 9 : 14}
        textAnchor="middle"
        fontFamily="Poppins"
        fontWeight="500"
        style={{ cursor: "pointer" }}
        onMouseEnter={(e) => isTruncated && showTooltip?.(e, fullValue)}
        onMouseLeave={() => hideTooltip?.()}
      >
        {displayValue}
      </text>
    </g>
  );
};

const formatYAxisValue = (value) => {
  return Number(value).toLocaleString();
};

const makeCustomPinkLabel =
  (isCompact, showTooltip, hideTooltip) => (props) => {
    const { x, y, width, value } = props;

    if (x === undefined || y === undefined) return null;

    const cx = x + width / 2;
    const minHeightThreshold = 0.1;
    const adjustedY = value <= minHeightThreshold ? y - 12 : y - 4;

    return renderSpeechBubble({
      cx,
      adjustedY,
      value,
      fill: "rgba(255, 202, 241, 1)",
      isCompact,
      showTooltip,
      hideTooltip,
    });
  };

// "Total Monitoring Hours" always shows its bubble via the LabelList above —
// these two series reuse the exact same bubble renderer (so width/font/color
// all match, and positioning stays pixel-perfect against the bar), but only
// render for the hovered bar.
const makeHoverBubbleLabel =
  (seriesKey, fill, hovered, isCompact, showTooltip, hideTooltip) =>
  (props) => {
    const { x, y, width, value, index } = props;

    if (!hovered || hovered.dataKey !== seriesKey || hovered.index !== index) {
      return null;
    }
    if (x === undefined || y === undefined) return null;

    const cx = x + width / 2;
    const minHeightThreshold = 0.1;
    const adjustedY = value <= minHeightThreshold ? y - 12 : y - 4;

    return renderSpeechBubble({
      cx,
      adjustedY,
      value,
      fill,
      isCompact,
      showTooltip,
      hideTooltip,
    });
  };

const MonitoringStats = (props) => {
  const {
    data = [],
    dateRangeDisplay,
    isLoading = false,
    hasError = false,
    onBarClick,
  } = props;

  const isCompact = useMediaQuery("(max-width:1536px)");
  const { tooltip, showTooltip, hideTooltip } = useChartTooltip();
  const CustomPinkLabel = makeCustomPinkLabel(
    isCompact,
    showTooltip,
    hideTooltip,
  );

  const [hoveredBar, setHoveredBar] = useState(null);
  const HoverDeviceLabel = makeHoverBubbleLabel(
    "avgPerDevice",
    "rgba(203, 161, 255, 1)",
    hoveredBar,
    isCompact,
    showTooltip,
    hideTooltip,
  );
  const HoverPregnantWomanLabel = makeHoverBubbleLabel(
    "avgPerPregnantWoman",
    "rgba(255, 168, 146, 1)",
    hoveredBar,
    isCompact,
    showTooltip,
    hideTooltip,
  );

  const handleBarCellClick = (entry, value = entry.hours) => {
    if (!onBarClick || !entry?.id) return;
    onBarClick({
      id: entry.id,
      placeId: entry.placeId,
      value,
      chartType: "monitoring",
    });
  };

  // avgPerDevice / avgPerPregnantWoman come from the page (real zone/facility
  // API fields, or self-computed for district-level grouping); default to 0
  // for callers that don't supply them (e.g. the per-device drill-down).
  const chartData = data.map((entry) => ({
    ...entry,
    avgPerDevice: entry.avgPerDevice ?? 0,
    avgPerPregnantWoman: entry.avgPerPregnantWoman ?? 0,
  }));

  const maxValue = Math.max(...data.map((d) => d.hours || 0), 0);

  const getNiceStep = (value) => {
    if (value <= 0) return 1;

    const roughStep = value / 4;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalized = roughStep / magnitude;

    let niceNormalized;

    if (normalized <= 1) {
      niceNormalized = 1;
    } else if (normalized <= 1.5) {
      niceNormalized = 1.5;
    } else if (normalized <= 2) {
      niceNormalized = 2;
    } else if (normalized <= 2.5) {
      niceNormalized = 2.5;
    } else if (normalized <= 5) {
      niceNormalized = 5;
    } else {
      niceNormalized = 10;
    }

    return niceNormalized * magnitude;
  };

  const cleanStep = getNiceStep(maxValue);

  const yAxisMax = Math.ceil(maxValue / cleanStep) * cleanStep;

  const yTicks = [];

  for (let value = 0; value <= yAxisMax; value += cleanStep) {
    yTicks.push(value);
  }

  return (
    <div className="monitoring-stats-container">
      <ChartHoverTooltip tooltip={tooltip} />
      {/* Header Row */}
      <div className="monitoring-stats-header">
        <div className="header-left">
          <h3 className="stats-title">Monitoring Stats</h3>
          <p className="stats-subtitle">Total monitoring done by each device</p>
        </div>

        <div className="header-right">
          {dateRangeDisplay && (
            <div className="time-toggle-group">
              <div className="date-inline-display">
                <CalendarMonthIcon className="calendar-icon" />
                {dateRangeDisplay}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart Row */}
      {isLoading ? (
        <div style={{ height: "280px", position: "relative" }}>
          <Loading text="Loading Monitoring Stats..." />
        </div>
      ) : hasError ? (
        <ChartError />
      ) : data.length === 0 ? (
        <div style={{ height: "280px", position: "relative" }}>
          <NoData className="stats-no-data" />
        </div>
      ) : (
        <div className="chart-wrapper" style={{ overflowX: "auto" }}>
          <div
            style={{
              minWidth: isCompact
                ? Math.max(data.length * 100, 500)
                : Math.max(data.length * 180, 900),
            }}
          >
            <ResponsiveContainer width="100%" height={isCompact ? 210 : 300}>
              <BarChart
                data={chartData}
                barCategoryGap={isCompact ? 18 : 30}
                margin={
                  isCompact
                    ? { top: 40, right: 16, left: 16, bottom: 18 }
                    : { top: 55, right: 30, left: 30, bottom: 30 }
                }
                barSize={isCompact ? 10 : 16}
                onMouseDown={(_, e) => e.preventDefault()}
                onMouseLeave={() => setHoveredBar(null)}
              >
                <CartesianGrid vertical={false} stroke="#EFF1F3" />
                <XAxis
                  dataKey="id"
                  interval={0}
                  axisLine={false}
                  tickLine={false}
                  dy={15}
                  height={isCompact ? 40 : 60}
                  tick={({ x, y, payload }) => {
                    const value = payload.value ?? "";
                    const maxLength = Math.max(
                      10,
                      Math.floor(80 / Math.max(data.length, 1)),
                    );

                    const displayText =
                      value.length > maxLength
                        ? value.slice(0, maxLength) + "..."
                        : value;

                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          dx={-10}
                          dy={16}
                          textAnchor="middle"
                          fill="#8C8C8C"
                          fontSize={isCompact ? 10 : 16}
                          fontFamily="Poppins"
                          style={{ cursor: "pointer" }}
                          onMouseEnter={(e) =>
                            displayText !== value && showTooltip(e, value)
                          }
                          onMouseLeave={hideTooltip}
                        >
                          {displayText}
                        </text>
                      </g>
                    );
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  tick={({ x, y, payload }) => {
                    const value = payload.value ?? 0;
                    const numberPart = formatYAxisValue(value);
                    const fullValue = `${numberPart} hr`;

                    const maxDigits = 6;
                    const isTruncated =
                      numberPart.replace(/,/g, "").length > maxDigits;

                    const displayText = isTruncated
                      ? `${numberPart.slice(0, maxDigits)}... hr`
                      : fullValue;

                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          dx={0}
                          dy={4}
                          textAnchor="end"
                          fill="#8C8C8C"
                          fontSize={isCompact ? 10 : 16}
                          fontFamily="Poppins"
                          style={{
                            cursor: isTruncated ? "pointer" : "default",
                          }}
                          onMouseEnter={(e) =>
                            isTruncated && showTooltip(e, fullValue)
                          }
                          onMouseLeave={hideTooltip}
                        >
                          {displayText}
                        </text>
                      </g>
                    );
                  }}
                  ticks={yTicks}
                  domain={[0, yAxisMax]}
                />

                <Bar
                  dataKey="hours"
                  radius={[4, 4, 0, 0]}
                  minPointSize={2}
                  activeBar={false}
                  style={{ cursor: onBarClick ? "pointer" : "default" }}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill="#FFB9E4"
                      onClick={() => handleBarCellClick(entry)}
                    />
                  ))}
                  <LabelList dataKey="hours" content={<CustomPinkLabel />} />
                </Bar>
                <Bar
                  dataKey="avgPerDevice"
                  radius={[4, 4, 0, 0]}
                  minPointSize={2}
                  activeBar={false}
                  style={{ cursor: onBarClick ? "pointer" : "default" }}
                  onMouseEnter={(_, index) =>
                    setHoveredBar({ dataKey: "avgPerDevice", index })
                  }
                  onMouseLeave={() => setHoveredBar(null)}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`avg-device-cell-${index}`}
                      fill="#CBA1FF"
                      onClick={() =>
                        handleBarCellClick(entry, entry.avgPerDevice)
                      }
                    />
                  ))}
                  <LabelList
                    dataKey="avgPerDevice"
                    content={<HoverDeviceLabel />}
                  />
                </Bar>
                <Bar
                  dataKey="avgPerPregnantWoman"
                  radius={[4, 4, 0, 0]}
                  minPointSize={2}
                  activeBar={false}
                  style={{ cursor: onBarClick ? "pointer" : "default" }}
                  onMouseEnter={(_, index) =>
                    setHoveredBar({ dataKey: "avgPerPregnantWoman", index })
                  }
                  onMouseLeave={() => setHoveredBar(null)}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`avg-pregnant-cell-${index}`}
                      fill="#FFA892"
                      onClick={() =>
                        handleBarCellClick(entry, entry.avgPerPregnantWoman)
                      }
                    />
                  ))}
                  <LabelList
                    dataKey="avgPerPregnantWoman"
                    content={<HoverPregnantWomanLabel />}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="monitoring-stats-legend">
        <div className="legend-item">
          <span
            className="legend-swatch"
            style={{ backgroundColor: "#FFB9E4" }}
          />
          Total Monitoring Hours
        </div>
        <div className="legend-item">
          <span
            className="legend-swatch"
            style={{ backgroundColor: "#CBA1FF" }}
          />
          Avg Monitoring time per device
        </div>
        <div className="legend-item">
          <span
            className="legend-swatch"
            style={{ backgroundColor: "#FFA892" }}
          />
          Avg Monitoring time per pregnant woman
        </div>
      </div>
    </div>
  );
};

export default MonitoringStats;
